// Display-only UI. No cart, checkout, quote-submission or Shopify mutation transport.
export function findVariant(catalog, handle, options) {
  const family = catalog.families.find(f => f.handle === handle);
  if (!family || !Object.keys(options).length) return null;
  const matches = catalog.variants.filter(v => v.family_id === family.id &&
    Object.keys(v.options).length === Object.keys(options).length &&
    Object.entries(v.options).every(([key,value]) => options[key] === value));
  return matches.length === 1 ? matches[0] : null;
}
export function familyMatches(variants, filters) {
  return variants.some(v => Object.entries(filters).every(([key,value]) =>
    !value || v.attributes[key] === value));
}
export function displayAmount(variant, currency, snapshot, now = Date.now()) {
  if (!variant?.identity_verified || !variant.source_net) return null;
  if (currency === variant.source_currency) return {amount:variant.source_net,currency,kind:"source"};
  // Future trusted server display contract. This never authorizes a purchase.
  if (!["EUR","USD","TRY"].includes(currency) || !snapshot ||
      snapshot.variant_id !== variant.id || snapshot.source_net !== variant.source_net ||
      snapshot.source_currency !== variant.source_currency || snapshot.markup !== "0" ||
      snapshot.provider !== "TCMB ForexSelling" || !snapshot.publication_id ||
      !Number.isFinite(Date.parse(snapshot.published_at)) ||
      Date.parse(snapshot.published_at) > now ||
      Date.parse(snapshot.valid_until)-Date.parse(snapshot.published_at)>3*24*60*60*1000 ||
      !Number.isFinite(Date.parse(snapshot.valid_until)) ||
      now >= Date.parse(snapshot.valid_until)) return null;
  const amount = snapshot.display_net?.[currency];
  if (typeof amount !== "string" || !/^[0-9]+\.[0-9]{2}$/.test(amount) || Number(amount) <= 0) return null;
  return {amount,currency,kind:"indicative",published_at:snapshot.published_at};
}
export function quoteDraft(variant, quantity, notes = "") {
  if (!variant?.identity_verified) return {error:"draft_blocked"};
  if (!/^[1-9][0-9]{0,3}$/.test(String(quantity))) return {error:"invalid_quantity"};
  return {status:"local_draft_not_sent",variant_id:variant.id,sku:variant.sku,
    options:variant.options,quantity:Number(quantity),notes:String(notes).slice(0,1000),
    quote_authorized:false,purchase_authorized:false};
}
export function optionURL(href, selectedIds) {
  if (!selectedIds.length || selectedIds.some(id => !/^[0-9]+$/.test(String(id)))) throw Error("Invalid option selection");
  const url = new URL(href);
  url.searchParams.delete("variant");
  url.searchParams.set("option_values",selectedIds.join(","));
  return url;
}

export function wireImageFallbacks(document) {
  for(const figure of document.querySelectorAll('[data-gallery-image]')){
    const image=figure.querySelector('img');
    const fallback=figure.querySelector('[data-image-error]');
    if(!image || !fallback) continue;
    const failed=()=>{image.hidden=true;fallback.hidden=false;};
    image.addEventListener('error',failed,{once:true});
    if(image.complete && image.naturalWidth===0) failed();
  }
}
export function wireNavigation(document) {
  const toggle=document.querySelector('[data-menu-toggle]');
  const nav=document.querySelector('[data-primary-navigation]');
  const media=document.defaultView?.matchMedia?.('(max-width:850px)');
  if(!toggle || !nav || !media) return; // No-JS fallback keeps every link visible.
  let open=false;
  const update=()=>{toggle.hidden=!media.matches;nav.hidden=media.matches&&!open;toggle.setAttribute('aria-expanded',String(media.matches&&open));};
  const close=()=>{open=false;update();};
  toggle.addEventListener('click',()=>{open=!open;update();});
  document.addEventListener('keydown',event=>{if(event.key==='Escape'&&media.matches&&open){close();toggle.focus();}});
  nav.addEventListener('click',event=>{if(event.target.closest('a'))close();});
  media.addEventListener('change',close);
  update();
}

export async function mount(document, fetcher = globalThis.fetch) {
  wireNavigation(document);
  wireImageFallbacks(document);
  const messages=document.querySelector("[data-messages]")?.dataset || {};
  const msg=key=>messages[key] || "";
  let catalog;
  try {
    const response=await fetcher(document.body.dataset.catalogUrl,{credentials:"omit"});
    if (!response.ok) throw Error("Catalog unavailable");
    catalog=await response.json();
    if (catalog.checkout_currency!=="TRY" || catalog.catalog_includes_vat!==false) throw Error("Invalid display catalog");
  } catch { return; } // Liquid fallback retains disabled purchase and internal review.
  const currencySelect=document.querySelector("[data-currency]");
  const currentURL=new URL(document.location.href);
  if(["EUR","USD","TRY"].includes(currentURL.searchParams.get("display_currency"))) currencySelect.value=currentURL.searchParams.get("display_currency");
  const carryCurrency=()=>{
    const currency=currencySelect.value;
    for(const link of document.querySelectorAll('a[href]')){
      const raw=link.getAttribute('href');
      if(raw.startsWith('#')) continue;
      const url=new URL(link.href);
      if(url.origin!==document.location.origin) continue;
      url.searchParams.set('display_currency',currency);link.href=url.href;
    }
    for(const form of document.querySelectorAll('form[method="get"]')){
      let input=form.querySelector('input[name="display_currency"]');
      if(!input){input=document.createElement('input');input.type='hidden';input.name='display_currency';form.append(input);}
      input.value=currency;
    }
  };
  currencySelect.addEventListener('change',()=>{
    const url=new URL(document.location.href);url.searchParams.set('display_currency',currencySelect.value);
    document.defaultView.history.replaceState(null,'',url);carryCurrency();
  });carryCurrency();
  const product=document.querySelector("[data-product-family]");
  let variant=null;
  if (product) {
    const options=Object.fromEntries([...product.querySelectorAll("[data-option-name]")].map(el=>
      [el.dataset.optionName,el.querySelector('[aria-current="true"]')?.dataset.optionValue]));
    variant=findVariant(catalog,product.dataset.productFamily,options);
    product.dataset.variantId=variant?.id || '';
    const graphic=product.querySelector('[data-variant-graphic]');
    if(graphic){
      graphic.dataset.mappingKey=product.dataset.productFamily+'/'+(variant?.id || 'unresolved');
      graphic.addEventListener('error',()=>{graphic.hidden=true;},{once:true});
      if(variant && document.body.dataset.graphicsUrl){
        try{
          const response=await fetcher(document.body.dataset.graphicsUrl,{credentials:'omit'});
          const bindings=await response.json();
          if(!response.ok || bindings.source_version!==catalog.version)throw Error('Graphic provenance');
          const binding=bindings.variants.find(b=>b.variant_id===variant.id && b.family_id===variant.family_id);
          if(binding && binding.real_photography===true && binding.selected_file==='photo-'+variant.id+'.webp'){
            graphic.removeAttribute('srcset');graphic.src=new URL(binding.selected_file,graphic.src).href;graphic.alt=binding.alt;
          }
        }catch{ /* Keep deterministic illustrative fallback, no commercial permission. */ }
      }
    }
    const specReview=product.querySelector('[data-spec-review]');
    if(specReview) specReview.hidden=!(variant?.withheld_attributes?.length);
    const optionsReview=product.querySelector('[data-options-review]');
    if(optionsReview) optionsReview.hidden=!(variant?.withheld_attributes?.length);
    const productionNotice=product.querySelector("[data-production-notice]");
    if(productionNotice) productionNotice.hidden=variant?.sales_model!=="made_to_order";
    product.querySelector("[data-variant-state]").textContent=msg(!variant?"noCombination":variant.identity_verified?"verified":"review");
    const attributes=product.querySelector("[data-attributes]");
    for(const [key,value] of Object.entries(variant?.attributes || {})){
      if(!value) continue;
      const row=document.createElement('tr'),heading=document.createElement('th'),cell=document.createElement('td');
      heading.scope='row';heading.textContent=msg(key.replace(/_([a-z])/g,(_,c)=>c.toUpperCase())) || key;
      cell.textContent=value;row.append(heading,cell);attributes.append(row);
    }
    product.querySelector("[data-source-price]").textContent=variant?.identity_verified && variant.source_net?
      variant.source_net+" "+variant.source_currency+" \u00b7 "+msg("sourceLabel"):"";
    const update=()=>{
      const currency=document.querySelector("[data-currency]").value;
      const price=displayAmount(variant,currency,catalog.fx?.[variant?.id]);
      product.querySelector("[data-display-price]").textContent=price?
        (price.kind==="indicative"?"\u2248 ":"")+price.amount+" "+price.currency:msg("missingFx");
      product.querySelector("[data-fx-date]").textContent=price?.published_at?
        msg("fxLabel")+" \u00b7 "+price.published_at:"";
    };
    currencySelect.addEventListener("change",update);update();
    for(const link of product.querySelectorAll("[data-option-value]")) link.addEventListener("click",()=>{
      const url=new URL(link.href);const ids=url.searchParams.get("option_values").split(",");
      const next=optionURL(url.href,ids);next.searchParams.set("display_currency",currencySelect.value);link.href=next.href;
    });
    if(catalog.fx?.[variant?.id]){
      const timer=document.defaultView.setInterval(update,30000);
      document.defaultView.addEventListener("pagehide",()=>document.defaultView.clearInterval(timer),{once:true});
    }
    product.querySelector("[data-quote-form]").addEventListener("submit",event=>{
      event.preventDefault();
      const form=event.currentTarget;
      const result=quoteDraft(variant,form.elements.quantity.value,form.elements.notes.value);
      product.querySelector("[data-quote-output]").textContent=result.error?
        msg(result.error==="draft_blocked"?"draftBlocked":"invalidQuantity"):
        msg("draftPrepared")+"\n"+result.sku+"\n"+Object.entries(result.options).map(([k,v])=>k+": "+v).join("\n")+"\n"+msg("quantity")+": "+result.quantity+(result.notes?"\n"+result.notes:"");
    });
    const fields=product.querySelector("[data-quote-fields]");
    if(fields && variant?.identity_verified) fields.disabled=false;
  }
  if(product && document.body.dataset.contentUrl){
    try{
      const response=await fetcher(document.body.dataset.contentUrl,{credentials:'omit'});
      if(!response.ok) throw Error('Content unavailable');
      const content=await response.json();
      if(content.version!=='storefront-content-2026-v1' || content.source_version!==catalog.version) throw Error('Content provenance mismatch');
      const copy=content.families.find(f=>f.handle===product.dataset.productFamily);
      if(copy){
        product.querySelector('[data-short-description]').textContent=copy.short_description;
        product.querySelector('[data-long-description]').textContent=copy.long_description;
        for(const warning of copy.warnings){const li=document.createElement('li');li.textContent=warning;product.querySelector('[data-content-warnings]').append(li);}
      }
    }catch{ /* Source copy is optional; never grants commerce permissions. */ }
  }
  // Correlated filters operate only on rendered families; native Shopify filters paginate server-side.
  const target=document.querySelector("[data-technical-filters]");
  if(target){
    const cards=[...document.querySelectorAll("[data-family]")];
    const ids=new Set(cards.map(card=>catalog.families.find(f=>f.handle===card.dataset.family)?.id));
    const variants=catalog.variants.filter(v=>ids.has(v.family_id));
    const filters={};
    const keys=["voltage","pump_count","motor_kw","starting_method","cranking_current","battery_type","pressure_range","amperage","pole_count","transfer_type","charger_current"];
    for(const key of keys){
      const values=[...new Set(variants.map(v=>v.attributes[key]).filter(Boolean))].sort();
      if(values.length<2) continue;
      const label=document.createElement("label");label.textContent=msg(key.replace(/_([a-z])/g,(_,c)=>c.toUpperCase())) || key;
      const select=document.createElement("select");select.dataset.technicalKey=key;select.id='technical-filter-'+key;label.htmlFor=select.id;
      const all=document.createElement("option");all.value="";all.textContent=msg("all");select.append(all);
      for(const value of values){const option=document.createElement("option");option.value=value;option.textContent=value;select.append(option);}
      label.append(select);target.append(label);
      select.addEventListener("change",()=>{
        filters[key]=select.value;
        for(const card of cards){
          const family=catalog.families.find(f=>f.handle===card.dataset.family);
          card.hidden=!familyMatches(variants.filter(v=>v.family_id===family?.id),filters);
        }
        const status=document.querySelector("[data-filter-status]");
        status.textContent=msg("matches")+": "+cards.filter(c=>!c.hidden).length;
      });
    }
  }
}
if(typeof document!=="undefined") mount(document);
